import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Container, 
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  Grid,
  Card,
  CardActionArea,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  Fab,
  Tooltip,
  List,
  ListItemButton,
  ListItemText
} from '@mui/material';
import { useParams, useLocation } from 'react-router-dom';
import { useAppNavigation } from '../hooks/useAppNavigation';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import ShareIcon from '@mui/icons-material/Share';
import QrCodeIcon from '@mui/icons-material/QrCode';
import api, { directApi } from '../services/api';
import offlineApi from '../services/offlineApi';
import boxService from '../services/boxService';
import itemService from '../services/itemService';
import { invalidateSearchCache } from '../utils/searchCache';
import familyService from '../services/familyService';
import demoService from '../services/demoService';
import { placeholderImage, getBestItemImageUrl, getBestBoxImageUrl } from '../utils/imageUtils';
import OfflineImage, { clearLocalImageCache } from '../components/OfflineImageSimple';
import TagExtractionStatus from '../components/TagExtractionStatus';
import AIProcessingIndicator from '../components/AIProcessingIndicator';
import QRLabelPrint from '../components/QRLabelPrint';

// Choose the appropriate API instance based on environment
const apiInstance = process.env.REACT_APP_API_URL ? directApi : api;

const BoxDetail = () => {
  const navigate = useAppNavigation();
  const { boxId } = useParams();
  const location = useLocation();
  const [box, setBox] = useState(null);
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingBox, setDeletingBox] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);
  const [qrLabelOpen, setQrLabelOpen] = useState(false);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupDialogItems, setGroupDialogItems] = useState([]);
  
  // Check for upload success notification from localStorage
  useEffect(() => {
    const successData = localStorage.getItem('cubxly_upload_success');
    if (successData) {
      try {
        const data = JSON.parse(successData);
        // Verify data is for this box and reasonably recent (last 30 seconds)
        if (data.boxId === boxId && data.timestamp > Date.now() - 30000) {
          setUploadSuccess(data);
          // Clear the success data after reading it
          localStorage.removeItem('cubxly_upload_success');
        } else {
          // Clear stale data
          localStorage.removeItem('cubxly_upload_success');
        }
      } catch (e) {
        console.error('Error parsing upload success data:', e);
        localStorage.removeItem('cubxly_upload_success');
      }
    }
  }, [boxId]);
  
  // Auto-dismiss notification after 5 seconds
  useEffect(() => {
    if (uploadSuccess) {
      console.log('Setting auto-dismiss timer for notification');
      const timer = setTimeout(() => {
        console.log('Auto-dismissing notification');
        setUploadSuccess(null);
      }, 5000);
      
      // Cleanup function to clear the timeout if component unmounts or uploadSuccess changes
      return () => {
        console.log('Clearing notification timer');
        clearTimeout(timer);
      };
    }
  }, [uploadSuccess]);
  
  
  // Fetch box and its items
  useEffect(() => {
    const fetchBoxData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Check if offline mode is enabled or if device is actually offline
        const isOfflineMode = await offlineApi.isOfflineModeEnabled();
        const isActuallyOffline = !navigator.onLine;
        console.log(`BoxDetail: Offline mode status - enabled: ${isOfflineMode}, actually offline: ${isActuallyOffline}`);
        
        // Determine if we should use offline data
        const useOfflineData = isOfflineMode || isActuallyOffline;
        console.log(`BoxDetail: Using ${useOfflineData ? 'offline' : 'online'} mode`);
        
        let boxData = null;
        let itemsData = [];
        
        if (useOfflineData) {
          // Try to get from offline storage using the helper methods
          console.log(`BoxDetail: Getting box ${boxId} from offline API`);
          boxData = await offlineApi.getBox(boxId);
          if (!boxData) {
            throw new Error('Box not found in offline storage');
          }
          
          // Get items for this box using the helper method
          itemsData = await offlineApi.getItemsByBoxId(boxId) || [];
          console.log(`BoxDetail: Found ${itemsData.length} items for box ${boxId} in offline storage`);
        } else {
          try {
            // Check if there are newly uploaded items that require cache-busting
            const newItemIds = localStorage.getItem('cubxly_new_item_ids');
            const shouldCacheBust = newItemIds && JSON.parse(newItemIds).length > 0;
            
            // Get from API with smart cache-busting (only when needed)
            console.log(`BoxDetail: Getting box ${boxId} from API${shouldCacheBust ? ' (with cache-busting for new items)' : ''}`);
            const boxUrl = shouldCacheBust ? `/boxes/${boxId}` : `/boxes/${boxId}`;
            const response = shouldCacheBust 
              ? await apiInstance.get(boxUrl, { params: { _cacheBust: true } })
              : await apiInstance.get(boxUrl);
            boxData = response.data;
            
            // Get items for this box - use cache-busting only if there are new items
            try {
              console.log(`BoxDetail: Getting items for box ${boxId} with query parameter${shouldCacheBust ? ' (with cache-busting)' : ''}`);
              const itemsUrl = `/items?box_id=${boxId}`;
              const itemsResponse = shouldCacheBust
                ? await apiInstance.get(itemsUrl, { params: { _cacheBust: true } })
                : await apiInstance.get(itemsUrl);
              itemsData = Array.isArray(itemsResponse.data) ? itemsResponse.data : [];
              console.log(`BoxDetail: Found ${itemsData.length} items using query parameter`);
            } catch (itemsError) {
              console.warn(`Error getting items with query parameter: ${itemsError.message}. Trying alternative endpoint...`);
              
              // Fallback to the boxService method which uses a consistent endpoint pattern
              try {
                const items = await boxService.getBoxItems(boxId);
                itemsData = Array.isArray(items) ? items : [];
                console.log(`BoxDetail: Found ${itemsData.length} items using boxService fallback`);
              } catch (fallbackError) {
                console.error('Both item fetch methods failed:', fallbackError);
                // Continue with empty items array, but don't fail the whole page
              }
            }
            
            // Cache data in background - don't await these operations
            if (boxData) {
              console.log('BoxDetail: Background caching box data for offline use');
              Promise.resolve().then(async () => {
                try {
                  await offlineApi.addOrUpdateBox(boxData);
                } catch (e) {
                  console.warn('Failed to cache box data:', e);
                }
              });
            }
            
            // Batch cache items in the background - don't await these operations
            if (itemsData && itemsData.length > 0) {
              console.log(`BoxDetail: Background caching ${itemsData.length} items for offline use`);
              Promise.resolve().then(async () => {
                try {
                  // Cache items in batches for better performance
                  for (const item of itemsData) {
                    await offlineApi.addOrUpdateItem(item);
                  }
                } catch (e) {
                  console.warn('Failed to cache items data:', e);
                }
              });
            }
          } catch (apiError) {
            console.error(`Error fetching box data from API: ${apiError.message}`);
            // Try to fall back to offline data if API fails
            try {
              console.log(`BoxDetail: Falling back to offline data for box ${boxId}`);
              boxData = await offlineApi.getBoxById(boxId);
              if (!boxData) {
                throw new Error('Box not found in offline storage');
              }
              itemsData = await offlineApi.getItemsByBoxId(boxId) || [];
            } catch (offlineError) {
              console.error(`Offline fallback also failed: ${offlineError.message}`);
              throw apiError; // Re-throw the original error
            }
          }
        }
        
        // Set the state with our fetched data
        if (boxData) {
          console.log(`BoxDetail: Box data loaded. Items count: ${itemsData.length}`);
          setBox(boxData);
          setItems(itemsData || []);
          
          // Check for multi-item detection in demo mode on initial load
          if (demoService.isDemoMode() && itemsData?.length > 0) {
            const hasMultiItemDetection = itemsData.some(item => item.shared_image_id);
            if (hasMultiItemDetection) {
              console.log('[Demo] Multi-item detection found on load - marking multi_item feature as completed');
              demoService.markFeatureCompleted('multi_item');
            }
          }
          
          // Clear the new item IDs flag after successful data load to prevent unnecessary cache-busting
          const newItemIds = localStorage.getItem('cubxly_new_item_ids');
          if (newItemIds) {
            localStorage.removeItem('cubxly_new_item_ids');
            console.log('BoxDetail: Cleared new item IDs flag after successful data load');
          }
        } else {
          // If we couldn't find the box data, show an error
          console.error(`BoxDetail: Box ${boxId} not found`);
          setError('Box not found. Please check your connection or try again.');
        }
      } catch (error) {
        console.error('BoxDetail: Error fetching box data:', error);
        setError('Failed to load box data. Please check your connection or try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    if (boxId) {
      fetchBoxData();
    }
  }, [boxId]);
  
  // Poll for AI processing status updates
  useEffect(() => {
    const processingItems = items.filter(item => 
      item.ai_processing_status === 'pending' || item.ai_processing_status === 'processing'
    );
    
    if (processingItems.length > 0) {
      console.log(`Polling for ${processingItems.length} items still processing...`);
      const interval = setInterval(async () => {
        try {
          // Refresh items data to get updated AI processing status
          const timestamp = new Date().getTime();
          const itemsResponse = await apiInstance.get(`/items?box_id=${boxId}&_t=${timestamp}`);
          const updatedItems = Array.isArray(itemsResponse.data) ? itemsResponse.data : [];
          
          // Check if any processing status changed or image_url changed
          const changedItems = updatedItems.filter(updatedItem => {
            const currentItem = items.find(item => item.id === updatedItem.id);
            if (!currentItem) return false;
            return currentItem.ai_processing_status !== updatedItem.ai_processing_status ||
                   currentItem.image_url !== updatedItem.image_url;
          });
          
          if (changedItems.length > 0) {
            console.log('AI processing status or image_url updated for some items:', changedItems.map(i => i.id));
            
            // Clear local image cache for items whose image_url changed
            // This ensures the new cropped image is fetched instead of showing cached group photo
            changedItems.forEach(updatedItem => {
              const currentItem = items.find(item => item.id === updatedItem.id);
              if (currentItem && currentItem.image_url !== updatedItem.image_url) {
                console.log(`Clearing image cache for item ${updatedItem.id} - image_url changed from ${currentItem.image_url} to ${updatedItem.image_url}`);
                clearLocalImageCache(updatedItem.id);
              }
            });
            
            // Check for multi-item detection in demo mode
            // Items with shared_image_id indicate they were created from multi-item AI detection
            if (demoService.isDemoMode()) {
              const hasMultiItemDetection = updatedItems.some(item => item.shared_image_id);
              if (hasMultiItemDetection) {
                console.log('[Demo] Multi-item detection found - marking multi_item feature as completed');
                demoService.markFeatureCompleted('multi_item');
              }
            }
            
            setItems(updatedItems);
          }
        } catch (error) {
          console.warn('Failed to poll for processing status updates:', error);
        }
      }, 3000); // Check every 3 seconds
      
      return () => {
        console.log('Stopping AI processing status polling');
        clearInterval(interval);
      };
    }
  }, [items, boxId]);
  
  // Toggle item selection
  const toggleItemSelection = (itemId) => {
    if (selectedItems.includes(itemId)) {
      setSelectedItems(selectedItems.filter(id => id !== itemId));
    } else {
      setSelectedItems([...selectedItems, itemId]);
    }
  };
  
  // Handle item click
  const handleItemClick = (itemId) => {
    if (selectMode) {
      toggleItemSelection(itemId);
    } else {
      navigate.to.ITEM_EDIT(itemId);
    }
  };

  // Open dialog listing all items that share a photo
  const openGroupDialog = (itemsInGroup) => {
    if (!itemsInGroup || itemsInGroup.length === 0) {
      return;
    }
    if (itemsInGroup.length === 1) {
      handleItemClick(itemsInGroup[0].id);
      return;
    }
    setGroupDialogItems(itemsInGroup);
    setGroupDialogOpen(true);
  };

  // Toggle selection mode
  const toggleSelectMode = () => {
    setSelectMode(!selectMode);
    setSelectedItems([]);
  };

  // Select all items
  const selectAllItems = () => {
    if (selectedItems.length === items.length) {
      // If all are selected, deselect all
      setSelectedItems([]);
    } else {
      // Otherwise select all
      const allIds = items.map(item => item.id);
      setSelectedItems(allIds);
    }
  };

  // Delete selected items
  const deleteSelectedItems = async () => {
    try {
      setIsLoading(true);
      
      // Check if we're in offline mode
      const isOfflineMode = await offlineApi.isOfflineModeEnabled();
      const isActuallyOffline = !navigator.onLine;
      const useOfflineApi = isOfflineMode || isActuallyOffline;
      
      console.log(`BoxDetail: Deleting items in ${useOfflineApi ? 'offline' : 'online'} mode`);
      
      // Delete items based on our connection state
      if (useOfflineApi) {
        // In offline mode, use the queue for deletions
        for (const itemId of selectedItems) {
          await offlineApi.queueRequest({
            method: 'DELETE',
            url: `/items/${itemId}`,
            data: null,
            timestamp: Date.now(),
            type: 'item_delete'
          });
          console.log(`BoxDetail: Queued deletion of item ${itemId} for sync`);
        }
        
        // Also update offline storage to reflect this change
        try {
          // Get current items from storage
          const allItems = await offlineApi.getOfflineData('items') || [];
          // Filter out the deleted items
          const filteredItems = allItems.filter(item => !selectedItems.includes(item.id));
          // Save updated items list back to storage
          await offlineApi.saveOfflineData('items', filteredItems);
          console.log(`BoxDetail: Updated offline storage after item deletion`);
        } catch (storageError) {
          console.error('BoxDetail: Error updating offline storage:', storageError);
        }
        
        // Invalidate search cache for offline deletions
        console.log('BoxDetail: Invalidating search cache for offline deletion');
        invalidateSearchCache();
      } else {
        // Online mode - use itemService which invalidates cache
        console.log(`BoxDetail: Deleting ${selectedItems.length} items through itemService`);
        for (const itemId of selectedItems) {
          await itemService.deleteItem(itemId);
          console.log(`BoxDetail: Deleted item ${itemId} (cache invalidated by service)`);
        }
      }
      
      // Update items list in UI
      const updatedItems = items.filter(item => !selectedItems.includes(item.id));
      setItems(updatedItems);
      setSelectedItems([]);
      setSelectMode(false);
      
      // Update the box data to reflect new item count - this is critical for accurate display
      if (box) {
        try {
          // First, update the box directly with accurate item count
          const updateData = {
            ...box, 
            items_count: updatedItems.length,
            item_count: updatedItems.length
          };
          
          if (useOfflineApi) {
            // Queue the box update for later sync
            await offlineApi.queueRequest({
              method: 'PUT',
              url: `/boxes/${boxId}`,
              data: updateData,
              timestamp: Date.now(),
              type: 'box_update'
            });
            console.log(`BoxDetail: Queued box update with new item count: ${updatedItems.length}`);
            
            // Update the box in offline storage
            const allBoxes = await offlineApi.getOfflineData('boxes') || [];
            const updatedBoxes = allBoxes.map(b => b.id === boxId ? {...b, items_count: updatedItems.length, item_count: updatedItems.length} : b);
            await offlineApi.saveOfflineData('boxes', updatedBoxes);
            console.log(`BoxDetail: Updated box ${boxId} in offline storage with new item count`);
          } else {
            // Make a PUT request to update the box with new item count
            await apiInstance.put(`/boxes/${boxId}`, updateData);
            console.log(`BoxDetail: Directly updated box ${boxId} with new item count: ${updatedItems.length}`);
            
            // Then immediately fetch the box again to refresh local data
            await apiInstance.get(`/boxes/${boxId}`);
          }
          
          // CRITICAL: Force a complete data refresh for all components
          // Use multiple approaches to ensure it's detected
          localStorage.removeItem('cubxly_albums_cache');
          
          // Set a timestamp to force refresh
          const timestamp = Date.now();
          localStorage.setItem('cubxly_data_refresh', timestamp);
          
          // Also set specific box update signals
          localStorage.setItem('cubxly_box_updated', boxId);
          localStorage.setItem('cubxly_box_item_count', updatedItems.length);
          
          // Use a new signal specific to item deletion
          localStorage.setItem('cubxly_items_deleted', timestamp);
          localStorage.setItem('cubxly_items_deleted_from_box', boxId);
          
          console.log(`Box ${boxId} updated in database to have ${updatedItems.length} items`);
        } catch (refreshError) {
          console.error('Error updating box data:', refreshError);
        }
      }
      
    } catch (error) {
      console.error('Error deleting items:', error);
      setError('Failed to delete items. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const shareBox = async (permissionLevel = 'view') => {
    try {
      setSharing(true);
      setError(null);
      
      await familyService.shareBox(boxId, permissionLevel);
      
      setShareSuccess(true);
      setTimeout(() => {
        setShareDialogOpen(false);
        setShareSuccess(false);
      }, 2000);
    } catch (error) {
      console.error('Error sharing box:', error);
      setError(error.message || 'Failed to share box with family');
    } finally {
      setSharing(false);
    }
  };
  
  // Delete box
  const deleteBox = async () => {
    try {
      setDeletingBox(true);
      
      console.log(`BoxDetail: Attempting to delete box ${boxId}`);
      
      // Use the boxService which properly handles both online and offline deletions
      const result = await boxService.deleteBox(boxId);
      
      if (result.success) {
        console.log(`BoxDetail: Successfully deleted box ${boxId}`);
      } else {
        console.warn(`BoxDetail: Box deletion returned non-success result:`, result);
        if (result.error) {
          throw new Error(result.error);
        }
      }
      
      // Navigate back to home after successful deletion
      navigate('/');
      
      // Set refresh signal
      localStorage.setItem('cubxly_data_refresh', Date.now().toString());
    } catch (error) {
      console.error('BoxDetail: Error deleting box:', error);
      setError('Failed to delete box. Please try again.');
      setDeleteDialogOpen(false);
    } finally {
      setDeletingBox(false);
    }
  };
  
  // Get sorted items - show each item individually (no grouping)
  const sortedItems = useMemo(() => {
    if (!items || items.length === 0) return [];
    return [...items].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [items]);
  
  // Handle back navigation - if coming from CreateBox via Locations, go back to Locations
  const handleBack = () => {
    const origin = location.state?.origin;
    if (origin?.from === 'locations') {
      // Restore the expanded state and navigate to Locations
      if (origin.expandedState) {
        sessionStorage.setItem('locations_expanded_state', JSON.stringify(origin.expandedState));
      }
      // Clear the origin data
      sessionStorage.removeItem('createbox_origin');
      navigate.to.LOCATIONS();
    } else {
      // Default back behavior
      window.history.back();
    }
  };
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', position: 'relative' }}>
      {/* App Bar */}
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="back"
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            {isLoading ? 'Loading...' : box ? `Box: ${box.name}` : 'Box Details'}
          </Typography>
        </Toolbar>
      </AppBar>
      
      {/* Main Content */}
      <Container component="main" sx={{ flexGrow: 1, py: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {isLoading ? (
          <CircularProgress sx={{ display: 'block', mx: 'auto', my: 8 }} />
        ) : box ? (
          <>
            {/* Box Information Card */}
            <Paper sx={{ p: 3, mb: 3 }}>
              {/* Box Image Display */}
              {(box.custom_image_id || box.customImageId) && (
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                  <Box
                    component="img"
                    src={getBestBoxImageUrl(box)}
                    alt={`Box: ${box.name}`}
                    onError={(e) => {
                      console.error('Box image failed to load:', e.target.src);
                      // Show placeholder image instead of hiding the element
                      e.target.src = '/logo192.png'; // Use app logo as fallback
                      e.target.style.opacity = '0.5'; // Make it slightly transparent to indicate it's a fallback
                    }}
                    onLoad={(e) => {
                      console.log('Box image loaded successfully:', e.target.src);
                      // Verify the image actually loaded content (not just a placeholder)
                      if (e.target.naturalWidth === 0 || e.target.naturalHeight === 0) {
                        console.warn('Box image appears to be empty or corrupted');
                        e.target.style.display = 'none';
                      }
                    }}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: 300,
                      width: 'auto',
                      height: 'auto',
                      borderRadius: 2,
                      boxShadow: 2,
                      objectFit: 'contain'
                    }}
                  />
                </Box>
              )}
              
              <Typography variant="h5" component="h2" gutterBottom>
                {box.name || 'Unnamed Box'}
              </Typography>
              
              <Typography variant="body1" color="text.secondary" paragraph>
                {box.description || 'No description provided'}
              </Typography>
              
              {/* Location Display */}
              {(box.location_full || box.location) && (
                <Box sx={{ mb: 2, p: 2, backgroundColor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                    📍 Location
                  </Typography>
                  <Typography variant="body2" color="text.primary">
                    {box.location_full || box.location}
                  </Typography>
                </Box>
              )}
              
              {/* Action Buttons - Responsive Grid Layout */}
              <Grid container spacing={1} sx={{ mt: 1 }}>
                {/* Row 1: Edit Box Details and QR Code (side by side on mobile) */}
                <Grid item xs={6} sm={6} md="auto">
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => navigate.to.BOX_EDIT(boxId)}
                    fullWidth
                    sx={{ justifyContent: 'center' }}
                  >
                    Edit Box
                  </Button>
                </Grid>
                
                <Grid item xs={6} sm={6} md="auto">
                  <Button
                    variant="outlined"
                    startIcon={<QrCodeIcon />}
                    onClick={() => setQrLabelOpen(true)}
                    fullWidth
                    sx={{ justifyContent: 'center' }}
                  >
                    QR Code
                  </Button>
                </Grid>
                
                {/* Row 2: Share with Family and Delete Box (side by side on mobile) */}
                <Grid item xs={6} sm={6} md="auto">
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<ShareIcon />}
                    onClick={() => setShareDialogOpen(true)}
                    fullWidth
                    sx={{ justifyContent: 'center' }}
                  >
                    Share
                  </Button>
                </Grid>
                
                <Grid item xs={6} sm={6} md="auto">
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteDialogOpen(true)}
                    fullWidth
                    sx={{ justifyContent: 'center' }}
                  >
                    Delete
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          
            {/* Items Section */}
            <Box sx={{ mb: 2 }}>
              {/* Action button row - only show when not in select mode */}
              {!selectMode && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <Button 
                    variant="outlined" 
                    onClick={toggleSelectMode}
                    startIcon={<SelectAllIcon />}
                  >
                    Select Items
                  </Button>
                </Box>
              )}
              
              {/* Selection Controls - Only visible when in select mode */}
              {selectMode && (
                <Paper elevation={1} sx={{ p: 2, mb: 3, position: 'sticky', top: 0, zIndex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Tooltip title={selectedItems.length === items.length ? 'Deselect All' : 'Select All'}>
                      <Button 
                        variant="outlined"
                        onClick={selectAllItems}
                        startIcon={<SelectAllIcon />}
                      >
                        {selectedItems.length === items.length ? 'Deselect All' : 'Select All'}
                      </Button>
                    </Tooltip>
                    
                    <Button 
                      variant="contained" 
                      color="error" 
                      onClick={deleteSelectedItems}
                      disabled={selectedItems.length === 0}
                      startIcon={<DeleteIcon />}
                    >
                      Delete ({selectedItems.length})
                    </Button>
                  </Box>
                </Paper>
              )}
            
              {/* Items Grid */}
              {items.length > 0 ? (
                <>
                  {selectMode ? (
                    <Grid container spacing={1}>
                      {items.map((item) => (
                        <Grid item xs={4} sm={3} md={2} key={item.id}>
                          <Card 
                            sx={{ 
                              height: '100%',
                              position: 'relative',
                              border: selectedItems.includes(item.id) 
                                ? '2px solid #1976d2' 
                                : 'none'
                            }}
                          >
                            <Checkbox
                              checked={selectedItems.includes(item.id)}
                              onChange={() => toggleItemSelection(item.id)}
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                zIndex: 1,
                                color: 'white',
                                backgroundColor: 'rgba(0,0,0,0.3)',
                                borderRadius: '50%',
                                p: 0.5,
                                m: 0.5
                              }}
                            />

                            <CardActionArea 
                              onClick={() => handleItemClick(item.id)}
                              sx={{ height: '100%' }}
                            >
                              <Box 
                                sx={{
                                  height: 0,
                                  paddingTop: '100%', // 1:1 aspect ratio
                                  position: 'relative',
                                  backgroundColor: 'rgba(0,0,0,0.04)'
                                }}
                              >
                                <OfflineImage
                                  src={getBestItemImageUrl(item)}
                                  itemId={item.id}
                                  alt={item.name || 'Item'}
                                  fallbackSrc={placeholderImage}
                                  prefetch={true}
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                  }}
                                />
                                
                                {/* AI Processing Status Overlay */}
                                {(item.ai_processing_status === 'pending' || item.ai_processing_status === 'processing') && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: 'white',
                                      zIndex: 2
                                    }}
                                  >
                                    <CircularProgress size={20} sx={{ color: 'white', mb: 0.5 }} />
                                    <Typography variant="caption" sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                      {item.ai_processing_status === 'pending' ? 'Queued...' : 'Processing...'}
                                    </Typography>
                                  </Box>
                                )}
                                
                              </Box>
                              <Box sx={{ p: 0.5 }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    textAlign: 'center',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}
                                >
                                  {item.name || `Item ${item.id}`}
                                </Typography>
                              </Box>
                            </CardActionArea>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Grid container spacing={1}>
                      {sortedItems.map((item) => (
                        <Grid item xs={4} sm={3} md={2} key={item.id}>
                          <Card 
                            sx={{ 
                              height: '100%',
                              position: 'relative'
                            }}
                          >
                            <CardActionArea 
                              onClick={() => openGroupDialog([item])}
                              sx={{ height: '100%' }}
                            >
                              <Box 
                                sx={{
                                  height: 0,
                                  paddingTop: '100%',
                                  position: 'relative',
                                  backgroundColor: 'rgba(0,0,0,0.04)'
                                }}
                              >
                                <OfflineImage
                                  src={getBestItemImageUrl(item)}
                                  itemId={item.id}
                                  alt={item.name || 'Item'}
                                  fallbackSrc={placeholderImage}
                                  prefetch={true}
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                  }}
                                />
                                
                                {/* AI Processing Status Overlay */}
                                {(item.ai_processing_status === 'pending' || item.ai_processing_status === 'processing') && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: 'white',
                                      zIndex: 2
                                    }}
                                  >
                                    <CircularProgress size={20} sx={{ color: 'white', mb: 0.5 }} />
                                    <Typography variant="caption" sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                      {item.ai_processing_status === 'pending' ? 'Queued...' : 'Processing...'}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                              <Box sx={{ p: 0.5 }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    textAlign: 'center',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}
                                >
                                  {item.name || `Item ${item.id}`}
                                </Typography>
                              </Box>
                            </CardActionArea>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </>
              ) : (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    No items in this box yet
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => navigate.to.ADD_ITEM(boxId)}
                    sx={{ mt: 2 }}
                  >
                    Add Items
                  </Button>
                </Paper>
              )}
            </Box>
          </>
        ) : (
          <Alert severity="warning">
            Box not found
          </Alert>
        )}
      </Container>
      
      {/* Floating Action Button to add items */}
      {box && items.length > 0 && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{ position: 'fixed', bottom: 72, right: 16 }}
          onClick={() => navigate.to.ADD_ITEM(boxId)}
        >
          <AddIcon />
        </Fab>
      )}
      
      {/* Success notification for newly added items */}
      {uploadSuccess && (
        <Box
          onClick={() => setUploadSuccess(null)} 
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            width: 'auto',
            maxWidth: '90%',
            cursor: 'pointer'
          }}
        >
          <Alert 
            severity="success"
            variant="filled"
            sx={{
              boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1,
              borderRadius: 2
            }}
          >
            <Typography variant="body1">
              {uploadSuccess.count} {uploadSuccess.count === 1 ? 'item' : 'items'} successfully added!
            </Typography>
          </Alert>
        </Box>
      )}
      
      {/* Tag Extraction Status */}
      {box && <TagExtractionStatus itemId={boxId} />}
      
      {/* AI Processing Status Banner - Shows when items are being processed */}
      {items.some(item => item.ai_processing_status === 'pending' || item.ai_processing_status === 'processing') && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            width: 'auto',
            maxWidth: '90%'
          }}
        >
          <AIProcessingIndicator 
            status={items.some(item => item.ai_processing_status === 'processing') ? 'processing' : 'pending'}
            isGroupPhoto={items.filter(item => 
              item.ai_processing_status === 'pending' || item.ai_processing_status === 'processing'
            ).length > 1}
            groupPhotoItemCount={items.filter(item => 
              item.ai_processing_status === 'pending' || item.ai_processing_status === 'processing'
            ).length}
            variant="inline"
          />
        </Box>
      )}
      
      {/* Share with Family Dialog */}
      <Dialog
        open={shareDialogOpen}
        onClose={() => !sharing && setShareDialogOpen(false)}
        aria-labelledby="share-dialog-title"
      >
        <DialogTitle id="share-dialog-title">
          Share Box with Family
        </DialogTitle>
        <DialogContent>
          {shareSuccess ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              Box shared successfully with your family!
            </Alert>
          ) : (
            <>
              <DialogContentText>
                Share "{box?.name}" with your family members. They will be able to view the box and its items.
              </DialogContentText>
              <DialogContentText sx={{ mt: 2, fontWeight: 'bold' }}>
                Choose permission level:
              </DialogContentText>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!shareSuccess && (
            <>
              <Button onClick={() => setShareDialogOpen(false)} disabled={sharing}>
                Cancel
              </Button>
              <Button 
                onClick={() => shareBox('view')} 
                color="primary"
                disabled={sharing}
              >
                {sharing ? <CircularProgress size={20} /> : 'Share (View Only)'}
              </Button>
              <Button 
                onClick={() => shareBox('edit')} 
                color="primary"
                variant="contained"
                disabled={sharing}
              >
                {sharing ? <CircularProgress size={20} /> : 'Share (Can Edit)'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Group item selection dialog for shared images */}
      <Dialog
        open={groupDialogOpen}
        onClose={() => setGroupDialogOpen(false)}
        aria-labelledby="group-dialog-title"
      >
        <DialogTitle id="group-dialog-title">
          Select item in shared photo
        </DialogTitle>
        <DialogContent dividers>
          <List>
            {groupDialogItems.map((gi) => (
              <ListItemButton
                key={gi.id}
                onClick={() => {
                  setGroupDialogOpen(false);
                  navigate.to.ITEM_EDIT(gi.id);
                }}
              >
                <ListItemText
                  primary={gi.name || `Item ${gi.id}`}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deletingBox && setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        disableEnforceFocus // Prevents focusing issues with ARIA
        keepMounted // Keeps the dialog in the DOM even when closed
      >
        <DialogTitle id="delete-dialog-title">
          Delete Box
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this box? This will remove the box and all associations with items. The items themselves will not be deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deletingBox}>
            Cancel
          </Button>
          <Button 
            onClick={() => {
              // Call deleteBox directly instead of as an event handler
              deleteBox().catch(err => {
                console.error('Error in dialog delete button handler:', err);
              });
            }} 
            color="error" 
            disabled={deletingBox}
          >
            {deletingBox ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* QR Label Print Dialog (includes Manage QR functionality) */}
      <QRLabelPrint
        open={qrLabelOpen}
        onClose={() => setQrLabelOpen(false)}
        boxId={boxId}
        boxName={box?.name}
        currentUuid={box?.uuid}
        onQrUpdate={(updatedBox) => {
          setBox(prev => ({ ...prev, uuid: updatedBox.uuid }));
        }}
      />
    </Box>
  );
};

export default BoxDetail;
